import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Chip,
} from '@mui/material';
import { Visibility, VisibilityOff, ArrowForward, PersonAdd, AccountBalance } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { stitchColors } from '../theme/tokens';

const registerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  userType: z.enum(['EO', 'EL', 'EX', 'EW']),
  subscription: z.enum(['FREE', 'PRO', 'PLUS']),
  referralCode: z.string().optional(),
});

type RegisterForm = z.infer<typeof registerSchema>;

const AUTH_HERO_IMAGE =
  "url('https://lh3.googleusercontent.com/aida-public/AB6AXuCwBeeE8zEq6KOf48iMoggxu6lV3XIePgydLswAiFLYiK5kvKmvaw3CM06RXMIBeGdlO1oUJIGZeqlXhgCqcTM0Szcfne8Tt9cWep-YbDFG1be0NtqT6YqFS-2az6xVfbqHgwNBF4S-lIdJ18A2ZQzRtjc3YJ_wLu-mKbC0oK4WH3nQmHXzE4xrwZzqJr-aTnqQ6KHzFBJjmWaKILZ4zzPU_-H4nk20fjYC8Mv42KCoy1QS-ksiQSSGXoGeTsIWvfyN-Ebiuyh5Refy')";

const Register: React.FC = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register: registerUser } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const referralCodeFromUrl = searchParams.get('ref') || '';

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<RegisterForm>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      userType: 'EO',
      subscription: 'FREE',
      referralCode: referralCodeFromUrl,
    },
  });

  useEffect(() => {
    if (referralCodeFromUrl) {
      setValue('referralCode', referralCodeFromUrl);
    }
  }, [referralCodeFromUrl, setValue]);

  const onSubmit = async (data: RegisterForm) => {
    try {
      setLoading(true);
      setError('');
      await registerUser(
        data.email,
        data.password,
        data.name,
        data.userType,
        data.subscription,
        data.referralCode
      );
      navigate('/');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const labelSx = {
    display: 'block',
    mb: 1,
    fontSize: '0.875rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase' as const,
    color: stitchColors.onSurface,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        bgcolor: stitchColors.background,
        color: stitchColors.onBackground,
      }}
    >
      <Box
        component="main"
        sx={{
          width: { xs: '100%', lg: '50%' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 2, sm: 4, lg: 8 },
          py: { xs: 4, lg: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <header>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: stitchColors.primary, mb: 2 }}>
              <AccountBalance sx={{ fontSize: 32 }} />
              <Typography
                sx={{
                  fontFamily: '"Newsreader", Georgia, serif',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                }}
              >
                Australian Wills
              </Typography>
            </Box>
            <Typography variant="h1" component="h1" sx={{ mb: 1 }}>
              Create Account
            </Typography>
            <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: stitchColors.onSurfaceVariant }}>
              Set up secure access to your estate workspace.
            </Typography>
          </header>

          {referralCodeFromUrl && (
            <Alert
              severity="info"
              icon={<PersonAdd />}
              sx={{
                borderRadius: 1,
                bgcolor: stitchColors.surfaceContainerLow,
                border: `1px solid ${stitchColors.outlineVariant}`,
                color: stitchColors.onBackground,
              }}
            >
              You&apos;ve been invited! Your referral code{' '}
              <Chip label={referralCodeFromUrl} size="small" sx={{ mx: 0.5, fontWeight: 600 }} /> has been applied.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ borderRadius: 1 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography component="label" htmlFor="reg-name" sx={labelSx}>
                  Full Name
                </Typography>
                <TextField
                  id="reg-name"
                  fullWidth
                  placeholder="Enter your full name"
                  {...register('name')}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="reg-email" sx={labelSx}>
                  Email
                </Typography>
                <TextField
                  id="reg-email"
                  fullWidth
                  type="email"
                  autoComplete="email"
                  placeholder="Enter your email"
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="reg-password" sx={labelSx}>
                  Password
                </Typography>
                <TextField
                  id="reg-password"
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Enter your password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              <Box>
                <Typography component="label" htmlFor="reg-usertype" sx={labelSx}>
                  I am a
                </Typography>
                <FormControl fullWidth>
                  <Controller
                    name="userType"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} id="reg-usertype" displayEmpty>
                        <MenuItem value="EO">Estate Owner (Creating my will)</MenuItem>
                        <MenuItem value="EL">Estate Lawyer</MenuItem>
                        <MenuItem value="EX">Estate Executor</MenuItem>
                        <MenuItem value="EW">Estate Witness</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Box>

              <Box>
                <Typography component="label" htmlFor="reg-subscription" sx={labelSx}>
                  Subscription Plan
                </Typography>
                <FormControl fullWidth>
                  <Controller
                    name="subscription"
                    control={control}
                    render={({ field }) => (
                      <Select {...field} id="reg-subscription" displayEmpty>
                        <MenuItem value="FREE">FREE Plan</MenuItem>
                        <MenuItem value="PRO">PRO Plan</MenuItem>
                        <MenuItem value="PLUS">PLUS Plan</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Box>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={loading}
              endIcon={<ArrowForward />}
              sx={{
                mt: 3,
                py: 1.75,
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {loading ? 'Creating account…' : 'Create account'}
            </Button>

            <Typography
              sx={{
                mt: 3,
                textAlign: 'center',
                fontSize: '0.875rem',
                color: stitchColors.onSurfaceVariant,
              }}
            >
              Already have an account?{' '}
              <Link component={RouterLink} to="/login" fontWeight={600} sx={{ color: stitchColors.primary }}>
                Log in
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        component="aside"
        aria-hidden
        sx={{
          display: { xs: 'none', lg: 'block' },
          width: { lg: '50%' },
          position: 'relative',
          minHeight: { lg: '100vh' },
          bgcolor: stitchColors.surfaceVariant,
          borderLeft: `1px solid ${stitchColors.outlineVariant}`,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: AUTH_HERO_IMAGE,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(to top right, ${stitchColors.surfaceVariant}66, transparent)`,
          }}
        />
      </Box>
    </Box>
  );
};

export default Register;
